import { USER_WORKER_URL } from '../../js/config.js';
import { tokenManager } from '../../js/utils/TokenManager.js';
import { applyTranslationsToContainer, getCurrentLanguage } from '../../js/translations.js';
import { syncMembershipUsage } from '../../js/create/videoScheduleApi.js';

const MEMBERSHIP_NOTIFICATION_TYPES = new Set(['membership_first_purchase', 'membership_upgrade']);

/**
 * @param {any[]} notifications
 * @returns {boolean}
 */
function hasMembershipTierNotification(notifications) {
  if (!Array.isArray(notifications) || notifications.length === 0) return false;
  return notifications.some(notification => MEMBERSHIP_NOTIFICATION_TYPES.has(notification?.type));
}

/** @returns {HTMLElement} */
export function createNotificationIcon() {
  const notificationContainer = document.createElement('div');
  notificationContainer.className = 'notification-container';

  const notificationButton = document.createElement('a');
  notificationButton.href = '#';
  notificationButton.className = 'notification-button';
  notificationButton.setAttribute('aria-label', 'Notifications');
  notificationButton.setAttribute('tabindex', '0');

  const bellIcon = document.createElement('i');
  bellIcon.className = 'fas fa-bell notification-bell-icon';
  bellIcon.setAttribute('aria-hidden', 'true');

  const notificationBadge = document.createElement('span');
  notificationBadge.className = 'notification-badge';
  notificationBadge.textContent = '0';

  notificationButton.appendChild(bellIcon);
  notificationButton.appendChild(notificationBadge);
  notificationContainer.appendChild(notificationButton);

  return notificationContainer;
}

/**
 * @param {Element} navElement
 * @param {number} count
 */
export function updateNotificationCount(navElement, count) {
  const badge = navElement.querySelector('.notification-badge');
  if (!badge) return;

  if (count > 0) {
    badge.textContent = count > 99 ? '99+' : count.toString();
    badge.style.display = 'flex';
    badge.classList.add('has-notifications');
  } else {
    badge.style.display = 'none';
    badge.classList.remove('has-notifications');
  }
}

/** @param {Element} navElement */
function decrementNotificationCount(navElement) {
  const badge = navElement.querySelector('.notification-badge');
  if (!badge) return;

  const raw = badge.textContent.trim();
  const current = raw.endsWith('+') ? parseInt(raw, 10) || 99 : parseInt(raw, 10) || 0;
  updateNotificationCount(navElement, Math.max(0, current - 1));
}

/** @param {{ isLoggedIn: boolean }} options */
export async function fetchNotificationCount({ isLoggedIn }) {
  if (!isLoggedIn) return 0;

  try {
    const response = await tokenManager.authenticatedFetch(`${USER_WORKER_URL}/notifications/count`);

    if (response.ok) {
      const data = await response.json();
      return data.count || 0;
    }
  } catch (error) {
    console.error('Failed to fetch notification count:', error);
  }
  return 0;
}

const NOTIFICATION_PAGE_SIZE = 30;
const NOTIFICATION_SCROLL_LOAD_PX = 48;

/** @param {{ limit?: number, offset?: number }} [options] */
export async function fetchNotifications({ limit = NOTIFICATION_PAGE_SIZE, offset = 0 } = {}) {
  try {
    const response = await tokenManager.authenticatedFetch(`${USER_WORKER_URL}/notifications`, {
      method: 'POST',
      body: JSON.stringify({ limit, offset, unread_only: false })
    });

    if (response.ok) {
      const data = await response.json();
      const notifications = data.notifications || [];
      if (hasMembershipTierNotification(notifications)) {
        await syncMembershipUsage();
      }
      return {
        notifications,
        hasMore: data.has_more === true || (data.has_more !== false && notifications.length >= limit)
      };
    }
  } catch (error) {
    console.error('Failed to fetch notifications:', error);
  }
  return { notifications: [], hasMore: false };
}

/** @param {string} notificationId */
export async function markNotificationRead(notificationId) {
  try {
    await tokenManager.authenticatedFetch(`${USER_WORKER_URL}/notifications/read`, {
      method: 'POST',
      body: JSON.stringify({ notification_id: notificationId })
    });
  } catch (error) {
    console.error('Failed to mark notification as read:', error);
  }
}

/** @param {object} notification */
const getNotificationId = (notification) => notification.notification_id || notification.id || null;

/** @param {object} notification */
const isNotificationRead = (notification) => !!(notification.read || notification.is_read);

/**
 * @param {object} notification
 * @param {{ navigateToScenario: Function, navigateToCharacter: Function, navigateToCreateVideo?: Function, navigateToMyGallery?: Function }} handlers
 * @returns {(() => void) | null}
 */
const resolveNotificationAction = (notification, { navigateToScenario, navigateToCharacter, navigateToCreateVideo, navigateToMyGallery }) => {
  const rawParams = notification.params;
  const params = (rawParams && typeof rawParams === 'object' && !Array.isArray(rawParams))
    ? rawParams
    : (Array.isArray(rawParams) ? rawParams : []);
  const meta = notification.metadata && typeof notification.metadata === 'object' ? notification.metadata : {};

  if (notification.type === 'standalone_image_generation' || notification.type === 'standalone_video_generation') {
    if (typeof navigateToMyGallery === 'function') return () => navigateToMyGallery();
    return () => { window.location.href = '/my-gallery.html'; };
  }

  if (notification.type === 'character_generation_failed') {
    return null;
  }

  if (notification.type === 'character_ready' || notification.type === 'outfit_generation_failed') {
    const characterId = meta.character_id || notification.character_id || params.character_id || params[1] || null;
    if (!characterId) return null;
    const isPrivate = meta.is_private === true || meta.private === true;
    const rawName = meta.character_name || meta.name || params.character_name || params[0] || '';
    const name = typeof rawName === 'string' && String(rawName) !== String(characterId) ? rawName : '';
    return () => navigateToCharacter(characterId, { isPrivate, name });
  }

  if (notification.type === 'scenario_published' || notification.type === 'scenario_created') {
    const scenarioId = meta.scenario_id || notification.scenario_id || params[1] || null;
    const characterId = meta.character_id || notification.character_id || params[2] || null;
    const scenarioName = meta.scenario_name || notification.title || params[0] || '';
    if (scenarioId) return () => navigateToScenario(scenarioId, characterId, scenarioName);
    if (characterId) return () => navigateToCharacter(characterId);
    return null;
  }

  if (notification.type === 'video_generation' && typeof navigateToCreateVideo === 'function') {
    const characterId = meta.character_id || params[1] || null;
    const outfitId = meta.outfit_id ?? params[2] ?? null;
    const actionId = meta.action_id ?? params[3] ?? null;
    if (characterId && actionId !== null && actionId !== undefined) {
      return () => navigateToCreateVideo(String(characterId), String(outfitId), String(actionId), 'upgrade');
    }
    return null;
  }

  return null;
};

/**
 * @param {HTMLElement} item
 * @param {object} notification
 * @param {Element | null | undefined} navElement
 */
async function markNotificationReadIfUnread(item, notification, navElement) {
  if (isNotificationRead(notification)) return;

  notification.read = true;
  item.classList.add('notification-item--read');

  const id = getNotificationId(notification);
  if (!id) return;

  await markNotificationRead(id);

  if (navElement) decrementNotificationCount(navElement);
}

/**
 * @param {object} notification
 * @param {{ navElement: Element, navigateToScenario: Function, navigateToCharacter: Function, navigateToCreateVideo?: Function, navigateToMyGallery?: Function }} options
 * @returns {HTMLElement}
 */
function createNotificationItem(notification, { navElement, navigateToScenario, navigateToCharacter, navigateToCreateVideo, navigateToMyGallery }) {
  const item = document.createElement('div');
  item.className = 'notification-item';
  if (isNotificationRead(notification)) item.classList.add('notification-item--read');

  item.addEventListener('mouseenter', () => item.classList.add('notification-item--hover'));
  item.addEventListener('mouseleave', () => item.classList.remove('notification-item--hover'));

  const priorityColors = {
    5: 'var(--color-error)',
    4: '#ff8800',
    3: '#ffaa00',
    2: '#00aa00',
    1: 'var(--color-text-dark)'
  };
  const priorityColor = priorityColors[notification.priority] || 'var(--color-text-dark)';

  const inner = document.createElement('div');
  inner.className = 'notification-item-inner';

  const priorityBar = document.createElement('div');
  priorityBar.className = 'notification-priority-bar';
  priorityBar.style.background = priorityColor;

  const body = document.createElement('div');
  body.className = 'notification-item-body';

  const titleEl = document.createElement('div');
  titleEl.className = 'notification-item-title';
  titleEl.textContent = notification.title;

  const messageEl = document.createElement('div');
  messageEl.className = 'notification-item-message';
  messageEl.textContent = notification.message;

  const dateEl = document.createElement('div');
  dateEl.className = 'notification-item-date';
  dateEl.textContent = new Date(notification.created_at).toLocaleString();

  body.appendChild(titleEl);
  body.appendChild(messageEl);
  body.appendChild(dateEl);

  inner.appendChild(priorityBar);
  inner.appendChild(body);
  item.appendChild(inner);

  const runAction = resolveNotificationAction(notification, { navigateToScenario, navigateToCharacter, navigateToCreateVideo, navigateToMyGallery });

  if (runAction) {
    const actionIndicator = document.createElement('button');
    actionIndicator.type = 'button';
    actionIndicator.className = 'notification-action-indicator';
    actionIndicator.textContent = '➙';
    actionIndicator.setAttribute('aria-label', 'Open notification');
    actionIndicator.setAttribute('tabindex', '0');
    actionIndicator.addEventListener('click', async (event) => {
      event.stopPropagation();
      await markNotificationReadIfUnread(item, notification, navElement);
      runAction();
    });
    item.appendChild(actionIndicator);
    item.classList.add('notification-item--actionable');
  }

  item.addEventListener('click', async () => {
    await markNotificationReadIfUnread(item, notification, navElement);
  });

  return item;
}

/**
 * @param {{ navElement: Element, navigateToScenario: Function, navigateToCharacter: Function, navigateToCreateVideo?: Function, navigateToMyGallery?: Function }} options
 */
export async function showNotificationPanel({ navElement, navigateToScenario, navigateToCharacter, navigateToCreateVideo, navigateToMyGallery }) {
  const existingPanel = document.getElementById('notification-panel');
  if (existingPanel) { existingPanel.remove(); return; }

  const panel = await createNotificationPanel({ navElement, navigateToScenario, navigateToCharacter, navigateToCreateVideo, navigateToMyGallery });
  document.body.appendChild(panel);
  panel.querySelector('.notification-panel-content')?.dispatchEvent(new Event('scroll'));
}

/**
 * @param {{ navElement: Element, navigateToScenario: Function, navigateToCharacter: Function, navigateToCreateVideo?: Function, navigateToMyGallery?: Function }} options
 */
async function createNotificationPanel({ navElement, navigateToScenario, navigateToCharacter, navigateToCreateVideo, navigateToMyGallery }) {
  const panel = document.createElement('div');
  panel.className = 'notification-panel';
  panel.id = 'notification-panel';

  const notificationButton = navElement.querySelector('.notification-button');
  const rect = notificationButton.getBoundingClientRect();

  const isMobile = window.innerWidth <= 1024;
  const panelWidth = isMobile ? Math.min(320, window.innerWidth - 20) : 350;
  const viewportPadding = 10;
  const maxLeft = Math.max(viewportPadding, window.innerWidth - panelWidth - viewportPadding);
  const idealLeft = isMobile
    ? window.innerWidth - panelWidth - viewportPadding
    : rect.right - panelWidth;
  const panelLeft = Math.min(maxLeft, Math.max(viewportPadding, idealLeft));

  panel.style.top = `${rect.bottom + 10}px`;
  panel.style.left = `${panelLeft}px`;
  panel.style.width = `${panelWidth}px`;

  const header = document.createElement('div');
  header.className = 'notification-panel-header';

  const titleEl = document.createElement('h3');
  titleEl.className = 'notification-panel-title';
  titleEl.dataset.translate = 'nav.notifications_title';

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'notification-panel-close-btn';
  closeBtn.setAttribute('aria-label', 'Close notifications');
  closeBtn.textContent = '×';

  header.appendChild(titleEl);
  header.appendChild(closeBtn);

  const content = document.createElement('div');
  content.className = 'notification-panel-content';

  const itemOptions = { navElement, navigateToScenario, navigateToCharacter, navigateToCreateVideo, navigateToMyGallery };
  const seenIds = new Set();
  let nextOffset = 0;
  let hasMore = true;
  let isLoadingMore = false;
  /** @type {IntersectionObserver | null} */
  let loadMoreObserver = null;

  const loader = document.createElement('div');
  loader.className = 'notification-panel-loader';
  loader.setAttribute('role', 'status');
  loader.setAttribute('aria-live', 'polite');
  const spinner = document.createElement('span');
  spinner.className = 'notification-panel-loader-spinner';
  spinner.setAttribute('aria-hidden', 'true');
  const loaderText = document.createElement('span');
  loaderText.dataset.translate = 'nav.notifications_loading';
  loader.appendChild(spinner);
  loader.appendChild(loaderText);

  const sentinel = document.createElement('div');
  sentinel.className = 'notification-panel-sentinel';
  sentinel.setAttribute('aria-hidden', 'true');

  const setLoaderVisible = (visible) => {
    loader.classList.toggle('is-visible', visible);
  };

  const footer = document.createElement('div');
  footer.className = 'notification-panel-footer';
  footer.appendChild(loader);
  footer.appendChild(sentinel);

  const appendNotifications = (notifications) => {
    for (const notification of notifications) {
      const id = getNotificationId(notification);
      if (id != null) {
        if (seenIds.has(id)) continue;
        seenIds.add(id);
      }
      content.appendChild(createNotificationItem(notification, itemOptions));
    }
    if (hasMore) content.appendChild(footer);
  };

  const stopPaging = () => {
    hasMore = false;
    loadMoreObserver?.disconnect();
    footer.remove();
    content.removeEventListener('scroll', handleContentScroll);
    content.removeEventListener('wheel', handleContentScroll);
  };

  const loadMore = async () => {
    if (isLoadingMore || !hasMore) return;
    isLoadingMore = true;
    setLoaderVisible(true);
    const page = await fetchNotifications({ limit: NOTIFICATION_PAGE_SIZE, offset: nextOffset });
    appendNotifications(page.notifications);
    nextOffset += page.notifications.length;
    hasMore = page.hasMore && page.notifications.length > 0;
    isLoadingMore = false;
    setLoaderVisible(false);
    if (!hasMore) stopPaging();
    else handleContentScroll();
  };

  const handleContentScroll = () => {
    if (!hasMore || isLoadingMore) return;
    const remaining = content.scrollHeight - content.scrollTop - content.clientHeight;
    if (remaining <= NOTIFICATION_SCROLL_LOAD_PX) {
      loadMore();
    }
  };

  const firstPage = await fetchNotifications({ limit: NOTIFICATION_PAGE_SIZE, offset: 0 });
  nextOffset = firstPage.notifications.length;
  hasMore = firstPage.hasMore && firstPage.notifications.length > 0;

  if (firstPage.notifications.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'notification-panel-empty';
    empty.dataset.translate = 'nav.notifications_empty';
    content.appendChild(empty);
  } else {
    appendNotifications(firstPage.notifications);
    if (hasMore) {
      content.addEventListener('scroll', handleContentScroll, { passive: true });
      content.addEventListener('wheel', handleContentScroll, { passive: true });
      loadMoreObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      }, { root: content, rootMargin: '0px 0px 80px 0px', threshold: 0 });
      requestAnimationFrame(() => {
        if (hasMore && footer.isConnected) loadMoreObserver?.observe(sentinel);
      });
    }
  }

  panel.appendChild(header);
  panel.appendChild(content);

  applyTranslationsToContainer(panel, 'nav', getCurrentLanguage());

  const removePanel = () => {
    loadMoreObserver?.disconnect();
    panel.remove();
  };

  closeBtn.addEventListener('click', () => removePanel());
  closeBtn.addEventListener('mouseenter', () => closeBtn.classList.add('notification-panel-close-btn--hover'));
  closeBtn.addEventListener('mouseleave', () => closeBtn.classList.remove('notification-panel-close-btn--hover'));

  setTimeout(() => {
    document.addEventListener('click', (e) => {
      if (!panel.contains(/** @type {Node} */ (e.target)) && !notificationButton.contains(/** @type {Node} */ (e.target))) {
        removePanel();
      }
    }, { once: true });
  }, 100);

  return panel;
}
